import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/PageHeader';
import { WalletFundForm } from '../components/StripeCheckout';
import styles from './Library.module.css';

// ================================================================
// Library
// ================================================================
export function Library() {
  const { user } = useAuth();
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    if (!user) { setGated(true); setLoading(false); return; }
    api.hasVoted().then(data => {
      if (!data.hasVoted) { setGated(true); setLoading(false); return; }
      api.getLibrary()
        .then(rows => setTrailers(Array.isArray(rows) ? rows : []))
        .catch(() => setTrailers([]))
        .finally(() => setLoading(false));
    }).catch(() => { setGated(true); setLoading(false); });
  }, [user]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (gated) return (
    <div>
      <PageHeader title="library" />
      <div className={styles.gatePage}>
        <div className={styles.gateCard}>
          <div className={styles.gateIcon}>🎬</div>
          <h2 className={styles.gateTitle}>We just started!</h2>
          <p className={styles.gateSub}>Check back here after voting closes to watch the winning films!</p>
          <div className={styles.gateBtns}>
            <Link to="/trailers" className="btn btn-magenta">Browse Trailers</Link>
            {!user && <Link to="/register" className="btn btn-outline-magenta">Sign Up</Link>}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="library" />
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Your Library</h1>
        <p className={styles.sub}>Films you helped greenlight.</p>
        {trailers.length === 0 ? (
          <div className={styles.gatePage}>
            <div className={styles.gateCard}>
              <div className={styles.gateIcon}>🎬</div>
              <h2 className={styles.gateTitle}>We just started!</h2>
              <p className={styles.gateSub}>Check back here after voting closes to watch the winning films!</p>
              <Link to="/trailers" className="btn btn-outline-magenta">Browse Trailers →</Link>
            </div>
          </div>
        ) : (
          <div className="grid-3">
            {trailers.map(t => (
              <div key={t.id} className={styles.libCard}>
                <img src={t.thumbnail_url} alt={t.title} />
                <div style={{ padding: '14px' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{t.title}</div>
                  <Link to={`/trailers/${t.slug}`} className="btn btn-magenta btn-sm">Watch</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ================================================================
// How It Works
// ================================================================
const MOVIE_LOVER_STEPS = [
  { title: 'Watch trailers',
    body: "Check out sizzle reels from filmmakers with all the talent and zero industry bigwigs in their back pocket. Every trailer is a movie ready to escape development purgatory. It just needs your vote." },
  { title: 'Vote with your wallet',
    body: "We've kept this part easy: $1 = 1 vote. Load up your voxpop wallet, or snag a ticket. No need to raid your 401k—this isn't crypto, just cinema." },
  { title: 'Watch what you helped create',
    body: "When a film hits its vote goal, it gets the greenlight. When it's ready, you stream it here. Your name may not be on the director's chair, but yeah— you helped make that." },
  { title: 'Earn perks and bragging rights',
    body: "Early voters get first-look access, behind-the-scenes footage, and the undeniable right to brag forever. We won't judge." },
];

const FILMMAKER_STEPS = [
  { title: 'Contact us using the form',
    body: 'Be prepared to send: your one-pager, signed NDA, full script in PDF, and a 2-minute or less trailer.' },
  { title: 'Build your audience',
    body: "If your project is accepted, we'll help you hype it. You focus on the story—we'll help you build the following." },
  { title: 'Get Funded',
    body: "We'll match you with a professional production team (not your roommate's friend's cousin) to finalize the budget, start filming, and make that sweet, sweet movie magic." },
  { title: 'Stream, baby stream',
    body: "Your film premieres right here on VoxPop, where people watch it. And cheer. And probably cry." },
];

const QA_PAIRS = [
  { q: 'Crowdfunding platform?',         a: 'Sure.' },
  { q: 'Streaming platform?',            a: 'Getting Warm.' },
  { q: 'Full-blown cinematic uprising?', a: 'BINGO.' },
];

// ── FIX: useRef called once on a parent element, not in a loop ─
function SpeechBubbleReveal() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState([false, false, false]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const rows = section.querySelectorAll('[data-bubble-row]');
    const observers = Array.from(rows).map((row, i) => {
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setVisible(v => { const n = [...v]; n[i] = true; return n; });
        },
        { threshold: 0.3 }
      );
      obs.observe(row);
      return obs;
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <div ref={sectionRef} className={styles.bubbleSection}>
      {QA_PAIRS.map((pair, i) => (
        <div key={i}
          data-bubble-row
          className={`${styles.bubbleRow} ${visible[i] ? styles.bubbleVisible : ''}`}
          style={{ transitionDelay: `${i * 0.15}s` }}>
          <div className={styles.bubbleQ}>{pair.q}</div>
          <div className={styles.bubbleA}>{pair.a}</div>
        </div>
      ))}
    </div>
  );
}

// Timeline grid layout moved to CSS (Library.module.css) so mobile media query
// can restructure to a single-column stack. Reveal animation still driven by
// IntersectionObserver via data-visible attribute.
function TimelineStep({ step, index, isLeft }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const card = (
    <div className={styles.timelineCard}>
      <div className={styles.timelineTitle}>{step.title}</div>
      <div className={styles.timelineBody}>{step.body}</div>
    </div>
  );

  return (
    <div
      ref={ref}
      className={styles.timelineItem}
      data-visible={visible ? 'true' : 'false'}
      data-side={isLeft ? 'left' : 'right'}
    >
      {/* Left column — shows card only when isLeft (desktop) */}
      <div className={styles.timelineLeftCol}>
        {isLeft && card}
      </div>

      {/* Center — step marker (desktop only) */}
      <div className={styles.timelineCenter}>
        <div className={styles.timelineMarker}>Step {index + 1}</div>
      </div>

      {/* Right column — shows card only when !isLeft (desktop) */}
      <div className={styles.timelineRightCol}>
        {!isLeft && card}
      </div>

      {/* Mobile left-rail timeline (Option A, H13) — hidden on desktop via CSS,
          shown below 768px. Numbered circle on magenta rail → title → body.
          Titles render in DM Sans bold sentence case to match desktop typography. */}
      <div className={styles.stepRow}>
        <div className={styles.stepMarker}>{index + 1}</div>
        <div className={styles.stepTitle}>{step.title}</div>
        <div className={styles.stepBody}>{step.body}</div>
      </div>
    </div>
  );
}

function TimelineSteps({ steps, audience }) {
  return (
    <div className={styles.timelineWrap}>
      <div className={styles.timelineAudience}>{audience} <span>→</span></div>
      <div className={styles.timeline}>
        <div className={styles.timelineLine} />
        {steps.map((step, i) => (
          <TimelineStep key={i} step={step} index={i} isLeft={i % 2 === 0} />
        ))}
      </div>
    </div>
  );
}

export function HowItWorks() {
  return (
    <div>
      <PageHeader title="how it works" />
      <div className={styles.page}>
        <SpeechBubbleReveal />
        <div className={styles.bodyBox}>
          <p>At VoxPop, we believe the future of film shouldn't be decided by a spreadsheet, an algorithm, or some guy named Steve who wears ties to work.</p>
          <p>Whether you're a filmmaker who's tired of chasing industry favors or a movie lover who's sick of the status quo, we're here to burn the old system down (metaphorically, of course).</p>
        </div>

        {/* Mobile-only audience label above the magenta pill (hidden on desktop) */}
        <div className={styles.audienceMobile}>I'm a movie lover <span>→</span></div>
        <div className={styles.magentaPill}>You're not just voting. You're making movies.</div>
        <TimelineSteps steps={MOVIE_LOVER_STEPS} audience="I'm a movie lover" />
        <div className={styles.gotItWrap}>
          <Link to="/trailers" className={styles.gotItPill}>
            Got it – Take me to the trailers!
          </Link>
        </div>

        {/* Mobile-only spacer between Movie Lover and Filmmaker sections */}
        <div className={styles.sectionGapMobile} aria-hidden="true" />

        {/* Mobile-only audience label above the magenta pill (hidden on desktop) */}
        <div className={styles.audienceMobile}>I'm a filmmaker <span>→</span></div>
        <div id="filmmaker" className={styles.magentaPill} style={{ scrollMarginTop: 24 }}>Tired of begging industry gatekeepers for scraps? You're in the right place.</div>
        <TimelineSteps steps={FILMMAKER_STEPS} audience="I'm a filmmaker" />
        <div className={styles.gotItWrap}>
          <Link to="/contact" className={styles.gotItPill}>
            Got it – Take me to the contact page!
          </Link>
        </div>
      </div>
      <div className="magenta-bar">
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 'clamp(18px,3vw,26px)', fontWeight: 800 }}>The revolution is already televised.</div>
            <div style={{ fontSize: 14, marginTop: 4, opacity: 0.8 }}>But we saved you a seat.</div>
          </div>
          <Link to="/register" className="btn btn-dark btn-lg">Sign Up</Link>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// Standings — only real films, no placeholder trailers
// ================================================================
const REMOVED_TITLES = ['black bag', 'red one', 'mortal engines'];
const SEED_STANDINGS = [
  { id: 1, slug: 'the-dark-knight',                        title: 'The Dark Knight',                           vote_count: 12400, vote_goal: 95000 },
  { id: 2, slug: 'mission-impossible-the-final-reckoning', title: 'Mission: Impossible – The Final Reckoning', vote_count: 8000,  vote_goal: 90000 },
  { id: 3, slug: 'dolls-of-voodoo',                        title: 'Dolls of Voodoo',                           vote_count: 3200,  vote_goal: 40000 },
];

export function Standings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getStandings()
      .then(data => {
        const filtered = Array.isArray(data)
          ? data.filter(r => !REMOVED_TITLES.includes(r.title?.toLowerCase?.()))
          : [];
        setRows(filtered.length > 0 ? filtered : SEED_STANDINGS);
      })
      .catch(() => setRows(SEED_STANDINGS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="scores & standings" />
      <div className={styles.page}>
        <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 20 }}>Updated every 30 seconds.</p>
        {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
          <>
            {/* Desktop table — hidden on mobile via CSS */}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>Rank</th><th>Film</th><th>Votes</th><th>Goal</th><th>Progress</th><th></th></tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    const pct = r.vote_goal ? Math.min(100, Math.round((r.vote_count / r.vote_goal) * 100)) : 0;
                    const done = pct >= 100;
                    return (
                      <tr key={r.id} className={done ? styles.rowDone : ''}>
                        <td className={styles.rankCell}>#{i + 1}</td>
                        <td><div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <Link to={`/trailers/${r.slug}`} className={styles.filmTitle}>{r.title}</Link>
                          {done && <span className="tag tag-teal">Goal reached</span>}
                        </div></td>
                        <td className={styles.numCell}>{(r.vote_count||0).toLocaleString()}</td>
                        <td className={styles.numCell} style={{ color:'var(--gray)' }}>{(r.vote_goal||0).toLocaleString()}</td>
                        <td className={styles.progressCell}>
                          <div className="progress-bar"><div className="progress-fill" style={{ width:`${pct}%` }} /></div>
                          <span className={styles.pctLabel}>{pct}%</span>
                        </td>
                        <td>{!done && <Link to={`/trailers/${r.slug}`} className="btn btn-magenta btn-sm">Vote</Link>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — hidden on desktop via CSS */}
            <div className={styles.standingsCardList}>
              {rows.map((r, i) => {
                const pct = r.vote_goal ? Math.min(100, Math.round((r.vote_count / r.vote_goal) * 100)) : 0;
                const done = pct >= 100;
                return (
                  <div key={r.id} className={`${styles.standingsCard} ${done ? styles.standingsCardDone : ''}`}>
                    <div className={styles.standingsCardHeader}>
                      <span className={styles.standingsCardRank}>#{i + 1}</span>
                      {done && <span className="tag tag-teal">Goal reached</span>}
                    </div>
                    <Link to={`/trailers/${r.slug}`} className={styles.standingsCardTitle}>{r.title}</Link>
                    <div className={styles.standingsCardMeta}>
                      <span><strong>{(r.vote_count||0).toLocaleString()}</strong> votes</span>
                      <span className={styles.standingsCardDot}>·</span>
                      <span style={{ color:'var(--gray)' }}>goal {(r.vote_goal||0).toLocaleString()}</span>
                    </div>
                    <div className={styles.standingsCardProgress}>
                      <div className="progress-bar"><div className="progress-fill" style={{ width:`${pct}%` }} /></div>
                      <span className={styles.pctLabel}>{pct}%</span>
                    </div>
                    {!done && (
                      <Link to={`/trailers/${r.slug}`} className="btn btn-magenta btn-sm" style={{ alignSelf: 'flex-start', marginTop: 4 }}>
                        Vote
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ================================================================
// Events — ALWAYS show "We just started!" empty state
// ================================================================
export function Events() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader title="events" />
      <div className={styles.page}>
        <div className={styles.gatePage}>
          <div className={styles.gateCard}>
            <div className={styles.gateIcon}>🎬</div>
            <h2 className={styles.gateTitle}>We just started!</h2>
            <p className={styles.gateSub}>
              Check back here for announcements.<br />
              Just in case, we'll also send you a reminder.
            </p>
            <div className={styles.gateBtns}>
              <Link to="/trailers" className="btn btn-magenta">Browse Trailers</Link>
              {!user && <Link to="/register" className="btn btn-outline-magenta">Sign Up</Link>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// Merch
// ================================================================
export function Merch() {
  const { showToast } = useToast();
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notified, setNotified] = useState(false);
  // H18: track which product images failed to load so we can render a clean
  // fallback instead of broken img + alt text. Defensive against any third-party
  // image URL going dead (Unsplash photos get removed/restricted occasionally).
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [products] = useState([
    { id:1, name:'VoxPop Cap',    desc:'Structured 6-panel cap with VoxPop wordmark.', price:1999, image:'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&q=70', discount:null },
    { id:2, name:'VoxPop Tee',    desc:'Classic crew-neck tee in black with embroidered VoxPop logo.', price:2999, image:'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&q=70', discount:null },
    { id:3, name:"Director's Cut Poster", desc:"Limited-edition 18\"×24\" art print. Tier 3+ members get 20% off.", price:3999, image:'https://images.unsplash.com/photo-1594909122845-11baa439b7bf?w=400&q=70', discount:'20% off for Tier 3+' },
    { id:4, name:'VoxPop Hoodie', desc:'Premium heavyweight hoodie. Tier 2+ members get 15% off.', price:5999, image:'/merch/voxpop_hoodie.png', discount:'15% off for Tier 2+' },
  ]);
  const handleNotify = e => {
    e.preventDefault();
    if (notifyEmail) { setNotified(true); showToast("We'll email you when merch drops!", 'success'); }
  };
  return (
    <div>
      <PageHeader title="merch" />
      <div className={styles.page}>
        <div className={styles.merchComingSoon}>
          <div className={styles.merchComingSoonText}>
            <h3 className={styles.merchComingSoonTitle}>🎬 Merch is coming soon!</h3>
            <p className={styles.merchComingSoonSub}>Drop your email and we'll let you know the moment it's ready.</p>
          </div>
          {!notified
            ? <form onSubmit={handleNotify} className={styles.merchNotifyForm}><input className="form-input" type="email" placeholder="your@email.com" value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} required /><button type="submit" className="btn btn-magenta">Notify Me</button></form>
            : <div className="alert alert-success">You're on the list!</div>}
        </div>
        <div className={styles.header}><span className="label-sm">preview — coming soon</span><h1 className={styles.bigHeadline}>MERCH</h1></div>
        <div className={styles.merchGrid}>
          {products.map(p => (
            <div key={p.id} className={styles.productCard}>
              <div className={styles.productImg}>
                {failedImages.has(p.id)
                  ? <div className={styles.productImgFallback} aria-label={p.name}><span>{p.name}</span></div>
                  : <img src={p.image} alt={p.name} loading="lazy"
                      onError={() => setFailedImages(prev => { const next = new Set(prev); next.add(p.id); return next; })} />
                }
                <div className={styles.productOverlay}>Coming Soon</div>
              </div>
              <div className={styles.productBody}>
                <div className={styles.productName}>{p.name}</div>
                <div className={styles.productDesc}>{p.desc}</div>
                <div className={styles.productPriceRow}><span className={styles.productPrice}>${(p.price/100).toFixed(2)}</span>{p.discount && <span className="tag tag-gold">{p.discount}</span>}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// Contact
// ================================================================
export function Contact() {
  const OPTIONS = ["I'm submitting the next cult classic","I have a question that keeps me up at night","I'm writing a love letter to cinema","I'm mildly confused","I'm Spielberg's secret burner account"];
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name:'', email:'', message:'' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async e => {
    e.preventDefault(); setSending(true); setError('');
    try { await api.contactSubmit({ name:form.name, email:form.email, subject:selected, message:form.message }); setSent(true); }
    catch (err) { setError(err.message); }
    finally { setSending(false); }
  };
  if (sent) return (
    <div><PageHeader title="contact" />
    <div style={{ textAlign:'center', padding:'80px 20px', maxWidth:600, margin:'0 auto' }}>
      <div style={{ fontSize:64 }}>🎬</div>
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:56, textTransform:'uppercase', marginTop:16 }}>We got it!</h2>
      <p style={{ color:'var(--gray)', marginTop:16, fontSize:18 }}>Thanks for reaching out. We'll get back to you in 3 business days.</p>
      <Link to="/" className="btn btn-outline-magenta" style={{ marginTop:28, display:'inline-flex' }}>Back to Home</Link>
    </div></div>
  );
  return (
    <div><PageHeader title="contact" />
    <div className={styles.contactPage}>
      <div className={styles.contactHeader}>
        <span className={styles.contactEyebrow}>Need help or have a question?</span>
        <h1 className={styles.contactHeadline}>Speak now or forever hold your screenplay</h1>
        <p className={styles.contactSub}>Whether you have a burning question, a weird idea, or just want to vent about the latest blockbuster you wasted three hours on, we're all ears 📣</p>
      </div>
      <div className={styles.contactDeal}>
        <h3 className={styles.contactDealLabel}>What's your deal?</h3>
        <div className={styles.contactOptions}>
          {OPTIONS.map((opt,i) => (
            <button key={i} type="button"
              className={`${styles.contactOption} ${selected===opt ? styles.contactOptionSelected : ''}`}
              onClick={() => setSelected(opt)}>{opt}</button>
          ))}
        </div>
      </div>
      {selected && (
        <div className={styles.contactFormWrap}>
          <h3 className={styles.contactFormLabel}>Spill your guts right here!</h3>
          <div className={styles.contactSelectedTag}>
            <span>{selected}</span>
            <button type="button" onClick={() => setSelected(null)} className={styles.contactDeselect}>✕</button>
          </div>
          <form onSubmit={submit} className={styles.contactForm}>
            {error && <div className="alert alert-error" style={{ marginBottom:12 }}>{error}</div>}
            <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={set('name')} required placeholder="Your name" /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={set('email')} required placeholder="your@email.com" /></div>
            <div className="form-group"><label className="form-label">Message</label><textarea className="form-input" value={form.message} onChange={set('message')} rows={6} style={{ resize:'vertical' }} required placeholder="Say what you gotta say..." /></div>
            <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={sending}>{sending ? 'Sending…' : 'Send it →'}</button>
          </form>
        </div>
      )}
    </div></div>
  );
}

// ================================================================
// Dashboard, Wallet, NotFound
// ================================================================
export function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.getDashboard().then(setData).catch(console.error).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  const stats = [{ label:'Total Votes Cast', val:data?.totalVotes||0 },{ label:'Films Backed', val:data?.projects?.length||0 },{ label:'Wallet Balance', val:`$${((data?.walletBalance||0)/100).toFixed(2)}` }];
  return (
    <div><PageHeader title="my account" />
    <div className={styles.page}>
      <div className={styles.statsRow}>{stats.map((s,i) => <div key={i} className={styles.statCard}><span className="label-sm">{s.label}</span><span className={styles.statValue}>{s.val}</span></div>)}</div>
      <div className={styles.section} style={{ marginTop:24 }}>
        <h2 className={styles.sectionTitle}>Your Projects</h2>
        {(data?.projects||[]).length===0
          ? <p className={styles.empty}>You haven't voted yet. <Link to="/trailers" style={{ color:'var(--red)' }}>Browse trailers →</Link></p>
          : <div className={styles.projectsList}>{data.projects.map((p,i) => <div key={i} className={styles.projectRow}><Link to={`/trailers/${p.slug}`} className={styles.projectTitle}>{p.title}</Link><div className={styles.projectMeta}><span>{p.votes} vote{p.votes!==1?'s':''}</span><span className="tag">{p.status}</span></div></div>)}</div>}
      </div>
    </div></div>
  );
}

export function WalletPage() {
  const { user, updateWalletBalance } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadWallet = () => { api.getWallet().then(setWallet).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { if (!user) { navigate('/login'); return; } loadWallet(); }, [user, navigate]);
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return (
    <div><PageHeader title="wallet" />
    <div className={styles.page} style={{ maxWidth:640 }}>
      <div className={styles.header}>
        <span className="label-sm">your balance</span>
        <h1 className={styles.pageTitle}>${((wallet?.balance||0)/100).toFixed(2)}</h1>
        <p className={styles.sub}>{Math.floor((wallet?.balance||0)/100)} votes available</p>
      </div>
      <div className={styles.section}><WalletFundForm onSuccess={(bal) => { updateWalletBalance(bal); loadWallet(); }} /></div>
    </div></div>
  );
}

export function NotFound() {
  return (
    <div style={{ textAlign:'center', padding:'100px 20px' }}>
      <div style={{ fontSize:72, fontFamily:'var(--font-display)', color:'var(--red)' }}>404</div>
      <h2 style={{ fontSize:24, fontWeight:700, marginBottom:12 }}>This film is in development hell.</h2>
      <p style={{ color:'var(--gray)', marginBottom:24 }}>The page you're looking for doesn't exist.</p>
      <Link to="/" className="btn btn-outline-magenta">Back to Home</Link>
    </div>
  );
}
